/**
 * Schedule display formatting
 * Strings the public schedule layouts show: an item's time without a timezone suffix (the zone is
 * named once for the whole schedule instead), day headings, and the one-line timezone caption.
 */

import {
  formatDayNumber,
  formatDayOfWeek,
  formatDayOfWeekShort,
  formatMonthLong,
  formatTimeRange,
} from "@/lib/utils/formatting";
import { getShortTimezoneName } from "@/lib/utils/timezones";
import { SCHEDULE_ALL_DAY_LABEL } from "../constants";
import type { ScheduleItemRow } from "../types/db-types";

/**
 * Formats an item's time range with no timezone suffix, for example "9 AM – 12 PM".
 *
 * @param item - The item; only `start_time` and `end_time` are read.
 * @returns The range, or `SCHEDULE_ALL_DAY_LABEL` when the item has no times.
 * @throws Never.
 */
export function formatScheduleItemTime(item: Pick<ScheduleItemRow, "start_time" | "end_time">): string {
  const range = formatTimeRange(item.start_time, item.end_time, " – ");
  return range || SCHEDULE_ALL_DAY_LABEL;
}

/**
 * Formats a day heading, for example "Tuesday, December 8".
 *
 * @param date - A `YYYY-MM-DD` date.
 * @returns The heading text.
 * @throws Never.
 */
export function formatScheduleDayHeading(date: string): string {
  return `${formatDayOfWeek(date)}, ${formatMonthLong(date)} ${formatDayNumber(date)}`;
}

/**
 * Formats a short day label for a day stepper, for example "Tue, Dec 8", or "Today, Tue, Dec 8".
 *
 * @param date - A `YYYY-MM-DD` date.
 * @param isToday - Whether `date` is today in the event's zone.
 * @returns The label text.
 * @throws Never.
 */
export function formatScheduleDayStepLabel(date: string, isToday: boolean): string {
  const label = `${formatDayOfWeekShort(date)}, ${formatScheduleMonthDay(date)}`;
  return isToday ? `Today, ${label}` : label;
}

/**
 * Formats a short month and day with no year, for example "Dec 8".
 *
 * @param date - A `YYYY-MM-DD` date.
 * @returns The month and day.
 * @throws Never.
 */
export function formatScheduleMonthDay(date: string): string {
  return `${formatMonthLong(date).slice(0, 3)} ${formatDayNumber(date)}`;
}

/**
 * Builds the caption that names the schedule's timezone once, for example "Times in EST".
 *
 * @param timeZone - The page's IANA timezone, or null for floating times.
 * @param date - A date in the schedule, so the abbreviation matches daylight saving on that day.
 * @returns The caption, or null when there is no zone or it has no short name.
 * @throws Never.
 */
export function getScheduleTimezoneCaption(timeZone: string | null, date: string | null): string | null {
  if (!timeZone) return null;
  const at = date ? new Date(`${date}T12:00:00`) : new Date();
  const short = getShortTimezoneName(timeZone, at);
  return short ? `Times in ${short}` : null;
}
